<!-- This is meant to emulate the donut-selector components, but it is a simulacrum only because it has to use the emoji-mart -->
<!-- functionality as well. Note that any updates to styling here may need to be done to donut-selector as well, or vice versa -->
<template>
  <div ref="selectorEl" :class="selectorClasses">
    <input v-if="props.formName" type="hidden" :name="props.formName" :value="props.modelValue">
    <div class="h-100p w-100p f-ac f-jc" @click.stop.prevent="onSelectorButtonClick">
      <div v-if="modelValue" class="t m-lx1"><em-emoji :id="modelValue" size="1.5em" set="apple" /></div>
      <i v-else class="mi gray">mood</i>
      <lucide-icon :name="chevronIcon" :size="20" :stroke-width="2" class="dark-gray pe-n" />
      <donut-upgrade-tag v-if="upgradeRequired" positioning="absolute" />
    </div>
    <!-- Teleport to allow it to not be cut off when in a modal -->
    <teleport to="content.donut">
      <div ref="emojiPickerEl" class="donut-emoji-selector" :style="menuStyle" />
    </teleport>
  </div>
</template>

<script setup>
  import { Data as emojiData, Picker } from 'emoji-mart';
  import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
  import { useStore } from 'vuex';

  const props = defineProps({
    modelValue: { type: String, default: null },
    formName: { type: String, default: null },
    categories: { type: Array, default: () => ['people', 'nature', 'foods', 'places', 'activity', 'objects', 'symbols', 'flags', 'customSlack'] },
    disabled: { type: Boolean, default: false },
    paywall: { type: Object, default: null },
  });
  const emit = defineEmits(['update:modelValue']);
  const { getters, dispatch } = useStore();

  const emojiPickerEl = ref(null);
  const selectorEl = ref(null);
  const chevronIcon = ref('ChevronDown');
  const menuStyle = ref({});

  const replaceName = (name) => {
    // With the exception of Donut, these overrides change the name to the shortcode humanized
    // instead of the name, since the shortcodes seem better in general.
    // This covers the food category of emoji 13.1.
    switch (name) {
      case 'Doughnut':
        return 'Donut';
      case 'Ear of Corn':
        return 'Corn';
      case 'Cooking':
        return 'Fried Egg';
      case 'Fish Cake with Swirl':
        return 'Fish Cake';
      case 'Hot Beverage':
        return 'Coffee';
      case 'Teacup Without Handle':
        return 'Tea';
      case 'Bottle with Popping Cork':
        return 'Champagne';
      default:
        return name;
    }
  };

  // The teleported picker is `fixed`, so it needs to move to stay with the button.
  // Open leftward unless that'd put the modal off-screen.
  const positionPickerMenu = () => {
    if (!selectorEl.value) return;
    const button = selectorEl.value.getBoundingClientRect();
    const menu = emojiPickerEl.value?.firstElementChild;
    // On first open, the picker menu modal is filled in asynchronously, possibly not finished until
    // after this has already fired. In practice this seems unnecessary but better safe than sorry.
    const menuWidth = menu?.offsetWidth || 352;
    const menuHeight = menu?.offsetHeight || 435;
    const pad = 8; // Just to keep the menu from touching the edge of the window.

    // Open left unless that makes the menu out of bounds, in which case open right.
    let left = button.right - menuWidth;
    if (left < pad) {
      left = Math.min(button.left, document.documentElement.clientWidth - menuWidth - pad);
    }
    // Open bottom unless that makes the menu out of bounds, in which case open top.
    let top = button.bottom + 2;
    if (top + menuHeight > document.documentElement.clientHeight - pad) {
      top = Math.max(pad, button.top - menuHeight - 2);
    }
    menuStyle.value = { top: `${top}px`, left: `${left}px` };
  };

  const closeMenu = () => {
    emojiPickerEl.value.replaceChildren();
    chevronIcon.value = 'ChevronDown';
    window.removeEventListener('scroll', positionPickerMenu, true);
    window.removeEventListener('resize', positionPickerMenu);
  };
  const onEmojiSelect = (emoji) => {
    emit('update:modelValue', { ...emoji, name: replaceName(emoji.name) });
    closeMenu();
  };
  const onClickOutside = () => {
    closeMenu();
  };
  const renderMenu = () => {
    if (!emojiPickerEl.value?.children?.length) {
      const pickerArgs = {
        parent: emojiPickerEl.value,
        theme: 'light',
        onEmojiSelect,
        onClickOutside,
        autoFocus: true,
        set: 'apple',
        skinTonePosition: 'none', // TBD: We might change this later on, but nothing in the product facilitates it yet
      };
      if (props.categories) pickerArgs.categories = props.categories;
      new Picker(pickerArgs); // eslint-disable-line no-new
      chevronIcon.value = 'ChevronUp';
      // The picker renders asynchronously; measure it on the next tick so positionPickerMenu sees its size.
      nextTick(positionPickerMenu);
      window.addEventListener('scroll', positionPickerMenu, true);
      window.addEventListener('resize', positionPickerMenu);
    }
  };

  // Paywalling
  const upgradeRequired = computed(() => getters.upgradeRequired(props.paywall));
  const onSelectorButtonClick = (e) => {
    if (upgradeRequired.value) {
      dispatch('openUpgradeModalIfRequired', props.paywall);
    } else if (emojiPickerEl.value?.children?.length) {
        closeMenu();
      } else {
        renderMenu();
      }
  };

  const selectorClasses = computed(() => {
    const classes = ['donut-emoji-selector-button'];
    if (upgradeRequired.value) classes.push('accommodate-upgrade-tag');
    if (props.disabled) classes.push('disabled');
    return classes;
  });

  onMounted(() => {
    if (props.modelValue) {
      const emoji = emojiData.emojis[props.modelValue];
      if (emoji) {
        emit('update:modelValue', { ...emoji, fromMount: true, name: replaceName(emoji.name) });
      }
    }
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', positionPickerMenu, true);
    window.removeEventListener('resize', positionPickerMenu);
  });
</script>
